import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import api from "../lib/api";
import { fmtH } from "../lib/planning";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
    ArrowLeft, Plane, Zap, Loader2, Trash2, Hourglass, Send,
} from "lucide-react";

const LEAVE_BADGES = {
    richiesta: { label: "IN ATTESA", cls: "bg-amber-100 text-amber-700" },
    approvata: { label: "APPROVATA", cls: "bg-emerald-100 text-emerald-700" },
    rifiutata: { label: "RIFIUTATA", cls: "bg-red-100 text-red-600" },
};
const EXTRA_BADGES = {
    assegnata: { label: "ASSEGNATA", cls: "bg-sky-100 text-sky-700" },
    da_confermare: { label: "IN VERIFICA", cls: "bg-amber-100 text-amber-700" },
    confermata: { label: "CONFERMATA", cls: "bg-emerald-100 text-emerald-700" },
    rifiutata: { label: "RIFIUTATA", cls: "bg-red-100 text-red-600" },
};
const fmtDate = (iso) => {
    try {
        return new Date(`${iso}T00:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
    } catch { return iso; }
};

export default function RichiesteDipendentePage() {
    const { user } = useAuth();
    const company = useCompany();
    const [enabled, setEnabled] = useState(null);
    const [tab, setTab] = useState("ferie");

    useEffect(() => {
        api.get("/presenze/richieste-config")
            .then((r) => setEnabled(!!r.data.enabled))
            .catch(() => setEnabled(false));
    }, []);

    return (
        <div className="min-h-screen bg-[#F4F8FD]" data-testid="richieste-page">
            <header className="border-b border-slate-200 bg-white">
                <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4 sm:px-6">
                    <div>
                        <Link to="/portale" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#376DB3]" data-testid="richieste-back-link">
                            <ArrowLeft className="h-3.5 w-3.5" /> Torna al portale
                        </Link>
                        <h1 className="mt-1 font-display text-xl font-bold text-slate-800">Ferie, permessi e straordinari</h1>
                        <p className="text-xs text-slate-500">{user?.name} · {company.ragione_sociale}</p>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
                {enabled === null ? (
                    <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
                ) : !enabled ? (
                    <div className="rounded-lg border border-slate-200 bg-white p-10 text-center shadow-sm" data-testid="richieste-disabled">
                        <Hourglass className="mx-auto h-10 w-10 text-[#376DB3]/50" />
                        <h2 className="mt-3 font-display text-lg font-bold text-slate-800">Funzione non ancora attiva</h2>
                        <p className="mt-2 text-sm text-slate-600">
                            L'invio di richieste dal portale non è ancora stato attivato dall'azienda.
                            Per ferie e permessi contatta l'ufficio del personale.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="mb-5 flex gap-2">
                            <button type="button" onClick={() => setTab("ferie")}
                                className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === "ferie" ? "bg-[#376DB3] text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
                                data-testid="tab-ferie">
                                <Plane className="h-4 w-4" /> Ferie e permessi
                            </button>
                            <button type="button" onClick={() => setTab("extra")}
                                className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === "extra" ? "bg-[#376DB3] text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
                                data-testid="tab-extra">
                                <Zap className="h-4 w-4" /> Straordinari
                            </button>
                        </div>
                        {tab === "ferie" ? <FerieTab /> : <ExtraTab />}
                    </>
                )}
            </main>
        </div>
    );
}

function FerieTab() {
    const [list, setList] = useState(null);
    const [type, setType] = useState("ferie");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [timeFrom, setTimeFrom] = useState("");
    const [timeTo, setTimeTo] = useState("");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/presenze/leave/mine");
            setList(data);
        } catch { setList([]); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const submit = async () => {
        if (!dateFrom) { toast.error("Inserisci la data di inizio"); return; }
        setSaving(true);
        try {
            await api.post("/presenze/leave/mine", {
                type, date_from: dateFrom, date_to: dateTo || dateFrom,
                time_from: timeFrom || null, time_to: timeTo || null, note: note || null,
            });
            toast.success("Richiesta inviata all'ufficio");
            setDateFrom(""); setDateTo(""); setTimeFrom(""); setTimeTo(""); setNote("");
            load();
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Errore nell'invio");
        } finally { setSaving(false); }
    };

    const cancel = async (l) => {
        try {
            await api.delete(`/presenze/leave/mine/${l.id}`);
            toast.success("Richiesta annullata");
            load();
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Errore");
        }
    };

    return (
        <>
            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="ferie-form-card">
                <h2 className="font-display text-sm font-bold uppercase tracking-wide text-slate-600">Nuova richiesta</h2>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Tipo</Label>
                        <select value={type} onChange={(e) => setType(e.target.value)}
                            className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="rich-type-select">
                            <option value="ferie">Ferie</option>
                            <option value="permesso">Permesso</option>
                        </select>
                    </div>
                    {type === "permesso" && (
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Dalle</Label>
                                <input type="time" value={timeFrom} onChange={(e) => setTimeFrom(e.target.value)}
                                    className="mt-1 h-10 w-full rounded-md border border-slate-200 px-1.5 text-sm" data-testid="rich-time-from" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Alle</Label>
                                <input type="time" value={timeTo} onChange={(e) => setTimeTo(e.target.value)}
                                    className="mt-1 h-10 w-full rounded-md border border-slate-200 px-1.5 text-sm" data-testid="rich-time-to" />
                            </div>
                        </div>
                    )}
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Dal *</Label>
                        <Input type="date" className="mt-1 h-10" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="rich-from-input" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Al</Label>
                        <Input type="date" className="mt-1 h-10" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="rich-to-input" />
                    </div>
                    <div className="sm:col-span-2">
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Motivo / note</Label>
                        <Input className="mt-1 h-10" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Facoltativo" data-testid="rich-note-input" />
                    </div>
                </div>
                <Button onClick={submit} disabled={saving} className="mt-4 w-full gap-2 bg-[#376DB3] py-5 text-white hover:bg-[#2c5a96]" data-testid="rich-submit-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Invia richiesta
                </Button>
            </div>

            <div className="mt-6 rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-100 px-4 py-3 font-display text-sm font-bold uppercase tracking-wide text-slate-600">Le mie richieste</div>
                {!list ? (
                    <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
                ) : list.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-500" data-testid="rich-empty">Non hai ancora inviato richieste.</div>
                ) : (
                    <ul className="divide-y divide-slate-100" data-testid="rich-list">
                        {list.map((l) => {
                            const b = LEAVE_BADGES[l.status] || {};
                            return (
                                <li key={l.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm" data-testid={`rich-item-${l.id}`}>
                                    <div>
                                        <span className={`mr-2 rounded px-1.5 py-0.5 text-[10px] font-bold ${l.type === "ferie" ? "bg-sky-100 text-sky-700" : "bg-violet-100 text-violet-700"}`}>
                                            {l.type === "ferie" ? "FERIE" : "PERMESSO"}
                                        </span>
                                        <span className="capitalize text-slate-700">
                                            {l.date_from === l.date_to ? fmtDate(l.date_from) : `${fmtDate(l.date_from)} → ${fmtDate(l.date_to)}`}
                                        </span>
                                        {l.time_from && <span className="ml-1 font-mono-numbers text-xs text-slate-500">({l.time_from}–{l.time_to || "…"})</span>}
                                        {l.note && <div className="text-xs text-slate-400">{l.note}</div>}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${b.cls}`}>{b.label}</span>
                                        {l.status === "richiesta" && (
                                            <button type="button" onClick={() => cancel(l)} className="text-slate-300 hover:text-red-500" title="Annulla richiesta" data-testid={`rich-cancel-${l.id}`}>
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </>
    );
}

function ExtraTab() {
    const [list, setList] = useState(null);
    const [worksites, setWorksites] = useState([]);
    const [date, setDate] = useState("");
    const [worksiteId, setWorksiteId] = useState("");
    const [hours, setHours] = useState("");
    const [timeFrom, setTimeFrom] = useState("");
    const [timeTo, setTimeTo] = useState("");
    const [description, setDescription] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/presenze/extra/mine");
            setList(data);
        } catch { setList([]); }
    }, []);
    useEffect(() => {
        load();
        api.get("/presenze/extra/worksites").then((r) => setWorksites(r.data)).catch(() => {});
    }, [load]);

    const submit = async () => {
        const h = parseFloat(String(hours).replace(",", "."));
        if (!date) { toast.error("Inserisci la data"); return; }
        if (Number.isNaN(h) || h <= 0) { toast.error("Inserisci le ore svolte"); return; }
        if (!description.trim()) { toast.error("Descrivi l'intervento svolto"); return; }
        setSaving(true);
        try {
            await api.post("/presenze/extra/mine", {
                date, worksite_id: worksiteId || null, hours: h,
                time_from: timeFrom || null, time_to: timeTo || null,
                description: description.trim(),
            });
            toast.success("Straordinario inviato: in attesa di conferma dell'ufficio");
            setDate(""); setWorksiteId(""); setHours(""); setTimeFrom(""); setTimeTo(""); setDescription("");
            load();
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Errore nell'invio");
        } finally { setSaving(false); }
    };

    const cancel = async (x) => {
        try {
            await api.delete(`/presenze/extra/mine/${x.id}`);
            toast.success("Straordinario annullato");
            load();
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Errore");
        }
    };

    return (
        <>
            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="extra-form-card">
                <h2 className="font-display text-sm font-bold uppercase tracking-wide text-slate-600">Segnala uno straordinario</h2>
                <p className="mt-1 text-xs text-slate-500">Usa questo modulo quando l'intervento non era stato assegnato in anticipo dall'ufficio.</p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Data *</Label>
                        <Input type="date" className="mt-1 h-10" value={date} onChange={(e) => setDate(e.target.value)} data-testid="rich-extra-date" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Cantiere</Label>
                        <select value={worksiteId} onChange={(e) => setWorksiteId(e.target.value)}
                            className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="rich-extra-ws">
                            <option value="">Nessuno / altro</option>
                            {worksites.map((w) => (
                                <option key={w.id} value={w.id}>
                                    {w.name}{w.client_name ? ` — ${w.client_name}` : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Ore svolte * <span className="font-normal normal-case text-slate-400">(centesimi: 1,25 = 1h e 15')</span></Label>
                        <Input type="number" min={0.01} step={0.01} className="mt-1 h-10" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="es. 1,25" data-testid="rich-extra-hours" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Dalle</Label>
                            <input type="time" value={timeFrom} onChange={(e) => setTimeFrom(e.target.value)}
                                className="mt-1 h-10 w-full rounded-md border border-slate-200 px-1.5 text-sm" data-testid="rich-extra-time-from" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Alle</Label>
                            <input type="time" value={timeTo} onChange={(e) => setTimeTo(e.target.value)}
                                className="mt-1 h-10 w-full rounded-md border border-slate-200 px-1.5 text-sm" data-testid="rich-extra-time-to" />
                        </div>
                    </div>
                    <div className="sm:col-span-2">
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Descrizione intervento *</Label>
                        <Input className="mt-1 h-10" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Es. pulizia straordinaria sala ricevimenti" data-testid="rich-extra-desc" />
                    </div>
                </div>
                <Button onClick={submit} disabled={saving} className="mt-4 w-full gap-2 bg-[#376DB3] py-5 text-white hover:bg-[#2c5a96]" data-testid="rich-extra-submit">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Invia segnalazione
                </Button>
            </div>

            <div className="mt-6 rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-100 px-4 py-3 font-display text-sm font-bold uppercase tracking-wide text-slate-600">I miei straordinari</div>
                {!list ? (
                    <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
                ) : list.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-500" data-testid="rich-extra-empty">Nessuno straordinario registrato.</div>
                ) : (
                    <ul className="divide-y divide-slate-100" data-testid="rich-extra-list">
                        {list.map((x) => {
                            const b = EXTRA_BADGES[x.status] || {};
                            return (
                                <li key={x.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm" data-testid={`rich-extra-item-${x.id}`}>
                                    <div className="min-w-0">
                                        <span className="capitalize text-slate-700">{fmtDate(x.date)}</span>
                                        <span className="ml-2 font-mono-numbers text-xs font-semibold text-slate-600">{fmtH(x.hours)} h</span>
                                        {x.worksite_name && <span className="ml-2 text-xs text-slate-500">{x.worksite_name}</span>}
                                        <div className="truncate text-xs text-slate-400" title={x.description}>{x.description}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${b.cls}`}>{b.label}</span>
                                        {x.status === "da_confermare" && (
                                            <button type="button" onClick={() => cancel(x)} className="text-slate-300 hover:text-red-500" title="Annulla" data-testid={`rich-extra-cancel-${x.id}`}>
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </>
    );
}
